import { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface CustomSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  id?: string;
  className?: string;
  size?: 'sm' | 'default';
  disabled?: boolean;
  variant?: 'light' | 'dark';
}

export function CustomSelect({
  value,
  onValueChange,
  options,
  placeholder = 'Seleccionar...',
  id,
  className = '',
  size = 'default',
  disabled = false,
  variant = 'light',
}: CustomSelectProps) {
  const selectedOption = options.find(o => o.value === value);
  const isPlaceholder = !selectedOption;
  const isDark = variant === 'dark';

  const triggerClasses = size === 'sm'
    ? 'py-1 pl-2.5 pr-7 text-xs'
    : 'py-2 pl-3 pr-9 text-sm';

  return (
    <Listbox value={value} onChange={onValueChange} disabled={disabled}>
      {({ open }) => (
        <div className={`relative ${className}`}>
          <Listbox.Button
            id={id}
            className={`
              relative w-full ${triggerClasses} text-left rounded-lg border
              ${isDark
                ? `bg-slate-800 ${open ? 'border-indigo-500 ring-2 ring-indigo-500/20' : 'border-slate-600 hover:border-slate-500'}`
                : `bg-white ${open ? 'border-indigo-500 ring-2 ring-indigo-100' : 'border-slate-300 hover:border-slate-400'}`
              }
              shadow-sm cursor-pointer focus:outline-none transition-all duration-150
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <span className={`block truncate ${isPlaceholder ? (isDark ? 'text-slate-500' : 'text-slate-400') : (isDark ? 'text-slate-200 font-medium' : 'text-slate-800 font-medium')}`}>
              {selectedOption?.label ?? placeholder}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
              <ChevronDown
                className={`${size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} ${isDark ? 'text-slate-400' : 'text-slate-400'} transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Listbox.Options className={`absolute z-50 mt-1.5 w-full min-w-[10rem] max-h-64 overflow-auto rounded-xl py-1.5 shadow-xl focus:outline-none text-sm ${isDark ? 'bg-slate-800 ring-1 ring-slate-600' : 'bg-white ring-1 ring-slate-200'}`}>
              {placeholder && (
                <Listbox.Option
                  value=""
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 pl-9 pr-4 ${
                      active ? (isDark ? 'bg-slate-700' : 'bg-slate-50') : ''
                    } ${isDark ? 'text-slate-500' : 'text-slate-400'} italic`
                  }
                >
                  {placeholder}
                </Listbox.Option>
              )}
              {options.map((option) => (
                <Listbox.Option
                  key={option.value}
                  value={option.value}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 pl-9 pr-4 ${
                      active
                        ? (isDark ? 'bg-indigo-600/20 text-indigo-300' : 'bg-indigo-50 text-indigo-900')
                        : (isDark ? 'text-slate-300' : 'text-slate-700')
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span className={`block truncate ${selected ? (isDark ? 'font-semibold text-indigo-400' : 'font-semibold text-indigo-700') : 'font-normal'}`}>
                        {option.label}
                      </span>
                      {selected && (
                        <span className={`absolute inset-y-0 left-0 flex items-center pl-2.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>
                          <Check className="w-4 h-4" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}
